import { Component, OnDestroy, OnInit } from '@angular/core';
import { MapService } from '../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { ComponentCanDeactivate } from '../../common/can-deactivate/component-can-deactivate';
import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Point } from 'geojson';
import { MapLayerControl } from '../../model/custom/map/map-layer-control';
import { Tenant } from '../../model/tenant';
import { LoggedUser } from '../../common/logged-user.service';
import { AuthGuard } from '../../common/auth-guard.service';
import { TenantsService } from '../../services/tenants.service';
import TinyColors from 'tinycolor2';
import * as moment from 'moment';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

enum CoverageLevel {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  USUAL = 'USUAL',
  BAD = 'BAD',
  UNKNOWN = 'UNKNOWN',
  ANY = 'ANY'
}

enum CoverageColor {
  EXCELLENT = '#25b18c',
  GOOD = '#68bc44',
  USUAL = '#b9d54e',
  BAD = '#ed7a4c',
  UNKNOWN = '#000000'
}

interface EdgeLine {
  type: string;
  properties: {
    'dash-line': number[];
    communications: number;
    color: string;
    distance: number;
    description: string;
    coverageStatus: CoverageLevel;
  };
  geometry: {
    coordinates: [number, number];
    type: string;
  };
}

@Component({
  selector: 'app-nettopo',
  templateUrl: './nettopo-map.component.html',
  styleUrls: ['./nettopo-map.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(500, style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate(500, style({ opacity: 0 }))
      ])
    ])
  ]
})
export class NettopoMapComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  public readonly DEFAULT_ZOOM = 14;
  public readonly DEFAULT_LATITUDE = 39.984;
  public readonly DEFAULT_LONGITUDE = -0.020;

  public tenants: Tenant[];
  public currentFilters: any = {
    tenantUuids: [],
    rangeDates: [moment.utc().subtract(1, 'hour').toDate(), moment.utc().toDate()]
  };

  public map: mapboxgl.Map;
  public representationVisibility: Map<string, boolean>;

  public signalsGraphData: any;
  public codeSynergyGraphData: any;
  public techSeriesGraphData: any;
  public distanceSeriesGraphData: any;
  public techXAxis: any;
  public techYAxis = {
    crosshair: { enabled: true, color: '#333' },
    lineWidth: 0,
    tickInterval: 25,
    reversedStacks: false,
    endOnTick: true,
    showLastLabel: true
  };
  public distanceXAxis = { categories: ['Min', 'Avg', 'Max'], crosshair: true };
  public distanceYaxis = { title: { text: 'km' } };
  public plotOptions = { pie: { dataLabels: { enabled: false } } };
  public plotSynergyOptions = { dependecywheel: { showInLegend: true, colorByPoint: true, legendType: 'point' } };
  public plotTechOptions = { column: { stacking: 'normal', borderWidth: 0, pointPadding: 0, groupPadding: 0.15 } };

  public filtersVisible = false;
  public displayDetails = false;

  public mapIsLoading: boolean;

  public data: any[] = [];
  public totalDocuments: number;

  private isFirstLoad = true;

  public codeColors: any[] = [];

  public nodes: any[] = [];
  public edges: any[] = [];
  public groupedNetCom: any[] = [];
  public codesSynergy: any[] = [];
  public codeDistance: any[] = [];

  public showNodeDialog = false;

  public layerForm: FormGroup;
  private activeLayerSubscription: Subscription;

  public coverageLevels = Object.values(CoverageLevel);
  public selectedIntegrationCode: string;
  public selectedDeviceSerialNumber: string;
  public selectedDeviceUuid: string | undefined;
  private currentPopup: mapboxgl.Popup | undefined;
  private alreadyLoadedNodes: string[] = [];

  private nodesListener = (e) => {
    const point: Point = e.features[0].geometry as unknown as Point;
    const coordinates = point.coordinates.slice();

    while( Math.abs(e.lngLat.lng - coordinates[0]) > 180 ) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    this.selectedIntegrationCode = e.features[0].properties.identifier;
    this.selectedDeviceUuid = e.features[0].properties.uuid;
    this.selectedDeviceSerialNumber = e.features[0].properties.serialNumber;
    this.paintActiveNode(this.selectedIntegrationCode);
    this.showPopup([coordinates[0], coordinates[1]], { ...e.features[0].properties });
  }

  private paintActiveNode(nodeIdentifier: string): void {
    this.map.setPaintProperty('nodes', 'circle-color', [
      'case',
      ['==', ['get', 'identifier'], nodeIdentifier],
      '#FF0000',
      ['get', 'color']
    ]);
  }

  private restartActiveNodes(): void {
    this.map.setPaintProperty('nodes', 'circle-color', ['get', 'color']);
  }

  constructor(
    private http: HttpClient,
    private mapService: MapService,
    public loggedUser: LoggedUser,
    private tenantsService: TenantsService,
    public authGuardService: AuthGuard,
    private fb: FormBuilder,
    private tran: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.representationVisibility = new Map<string, boolean>();
    this.loadFilters();
    this.buildMap();
    this.loadMapData();
    this.layerForm = this.fb.group({
      visibleLayer: [CoverageLevel.ANY]
    });
    this.activeLayerSubscription = this.layerForm.get('visibleLayer').valueChanges.subscribe((value: CoverageLevel) => {
      const selectedCoverageLevel = !value ? CoverageLevel.ANY : value;
      this.applyLayerFilter(selectedCoverageLevel);
    });
  }

  private applyLayerFilter(selectedCoverageLevel: CoverageLevel): void {
    this.coverageLevels.forEach(coverageLevel => {
      if( coverageLevel !== CoverageLevel.ANY ) {
        this.map.setLayoutProperty(
          `${ coverageLevel.toLowerCase() }-edges`,
          'visibility',
          selectedCoverageLevel === CoverageLevel.ANY || coverageLevel === selectedCoverageLevel ? 'visible' : 'none'
        );
      }
    });
    this.filtersVisible = false;
  }

  private loadFilters(): void {
    this.loggedUser.userObservable.asObservable().subscribe(user => this.currentFilters.tenantUuids.push(user.tenantUuid));
    if( this.authGuardService.superAdminRequired() ) {
      this.tenantsService.getTenants().subscribe(response => {
        this.tenants = response.tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      });
    }
  }

  private buildMap(): void {
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, this.DEFAULT_LATITUDE, this.DEFAULT_LONGITUDE, 'topoMap', true, { name: 'globe' });
    this.map.on('style.load', () => {
      this.map.setFog({}); // Set the default atmosphere style
    });
  }

  public loadMapData(): void {
    this.nodes = [];
    this.edges = [];
    this.codesSynergy = [];
    this.groupedNetCom = [];
    this.codeColors = [];
    this.codeDistance = [];
    this.alreadyLoadedNodes = [];
    this.mapIsLoading = true;
    this.isFirstLoad = true;
    this.closePopup();
    this.http.post<any>(`/catalog/reports/nettopo`, this.buildQuery()).subscribe(response => {
      this.drawData(response);
    });
  }

  private drawData(response: any): void {
    const buckets: any[] = response.aggregations.edges.buckets;

    this.data = [...this.data, ...buckets];

    buckets.forEach(edge => {
      const source = edge.max_doc.hits.hits[0]._source;

      if( !this.isFirstLoad ) {
        const totalFromCommunications = this.nodes[source.from]?.totalCommunications !== undefined
          ? this.nodes[source.from].totalCommunications + edge.doc_count
          : edge.doc_count;

        this.nodes = {
          ...this.nodes, [source.from]: {
            totalCommunications: totalFromCommunications,
            isImported: source.toIntegrationIdentifier === 'VIRTUAL_GW' || source.toIntegrationIdentifier === null,
            latitude: source.fromLatitude,
            longitude: source.fromLongitude,
            code: source.fromIntegrationCode,
            integrationIdentifier: source.fromIntegrationIdentifier,
            uuid: source.fromUuid,
            identifier: source.from,
            serialNumber: source.fromSerialNumber,
            toNodeId: source.to,
            lastCommunicationTimestamp: source['@timestamp']
          }
        };
      }

      if( this.isFirstLoad ) {
        const totalToCommunications = this.nodes[source.to]?.totalCommunications !== undefined
          ? this.nodes[source.to].totalCommunications + edge.doc_count
          : edge.doc_count;

        this.nodes = {
          ...this.nodes, [source.to]: {
            isGW: source.throughGw,
            isHUB: source.throughHub,
            totalCommunications: totalToCommunications,
            latitude: source.toLatitude,
            longitude: source.toLongitude,
            code: source.toIntegrationCode,
            integrationIdentifier: source.toIntegrationIdentifier,
            uuid: source.toUuid,
            identifier: source.to,
            serialNumber: source.toSerialNumber
          }
        };
      }

      if( !this.isFirstLoad ) {
        if( source.toLatitude !== null ) {
          this.edges[edge.key] = {
            origin: source.fromIntegrationCode,
            from: source.from,
            communications: edge.doc_count,
            throughGw: source.throughGw,
            throughHub: source.throughHub,
            line: [[source.fromLongitude, source.fromLatitude], [source.toLongitude, source.toLatitude]],
            distance: turf.length(turf.lineString([[source.fromLongitude, source.fromLatitude], [source.toLongitude, source.toLatitude]])),
            coverage: source.rssi
          };
          if( !this.codeDistance[source.fromIntegrationCode] ) {
            this.codeDistance[source.fromIntegrationCode] = [];
          }
          this.codeDistance[source.fromIntegrationCode].push(this.edges[edge.key].distance);
        }
      }
    });

    const colors = TinyColors('#407ec9').monochromatic(this.codeColors.length);

    Object.entries(this.codeColors).forEach(([key, value], index) => {
      value.color = colors[index].toHexString();
    });

    const edgeLines: EdgeLine[] = [];
    const nodePoints = [];

    Object.entries(this.edges).forEach(([key, value]) => {
      const [coverageStatus, coverageColor] = this.getCoverageStatusAndColor(value.coverage, value.origin);
      edgeLines.push(
        {
          type: 'Feature',
          properties: {
            coverageStatus,
            color: coverageColor,
            'dash-line': value.throughGw ? [0, 4, 3] : [1],
            description:
              '<strong>' + value.distance.toFixed(3) + ' km (' + value.origin + ') </strong>' +
              '<p>Total communications: ' + value.communications + '</p>',
            communications: value.communications,
            distance: value.distance
          },
          geometry: {
            type: 'LineString',
            coordinates: [value.line[1], value.line[0]]
          },
        }
      );
    });

    Object.entries(this.nodes).forEach(([key, value], index) => {
      const edgeToParent = Object.values(this.edges).find(edge => edge.from === value.identifier);
      nodePoints.push(
        {
          type: 'Feature',
          properties: {
            uuid: value.uuid,
            identifier: value.identifier,
            serialNumber: value.serialNumber,
            color: value.isGW ? '#03c211' : value.isHUB ? '#acb900' : value.isImported ? '#42bafb' : '#4264fb',
            isGw: value.isGW,
            coverage: edgeToParent?.coverage ?? 'unknown',
            distance: edgeToParent?.distance ?? 'unknown',
            totalCommunications: value.totalCommunications
          },
          geometry: {
            type: 'Point',
            coordinates: [value.longitude, value.latitude]
          }
        }
      );
    });

    if( this.map.loaded() ) {
      this.buildMapSourcesAndLayers(nodePoints, edgeLines);
      this.createListenersAndPopups();
    } else {
      this.map.on('load', () => {
        this.buildMapSourcesAndLayers(nodePoints, edgeLines);
        this.createListenersAndPopups();
      });
    }

    this.totalDocuments = response.hits.total.value;
    this.mapIsLoading = false;
    this.isFirstLoad = false;
  }

  private getCoverageStatusAndColor(coverage: number, integrationCode: string): [CoverageLevel, CoverageColor] {
    if( coverage == null ) {
      return [CoverageLevel.UNKNOWN, CoverageColor.UNKNOWN];
    }

    const getStatusAndColor = (thresholds: number[], signalStrength: number): [CoverageLevel, CoverageColor] => {
      if( signalStrength > thresholds[0] ) { return [CoverageLevel.EXCELLENT, CoverageColor.EXCELLENT]; }
      if( signalStrength > thresholds[1] ) { return [CoverageLevel.GOOD, CoverageColor.GOOD]; }
      if( signalStrength > thresholds[2] ) { return [CoverageLevel.USUAL, CoverageColor.USUAL]; }
      return [CoverageLevel.BAD, CoverageColor.BAD];
    };

    switch( integrationCode ) {
      case 'LORA':
        return getStatusAndColor([-100, -110, -120], coverage);
      case 'IOTSENS':
        return getStatusAndColor([-60, -70, -90], coverage);
      default:
        return [CoverageLevel.UNKNOWN, CoverageColor.UNKNOWN];
    }
  }

  private buildMapSourcesAndLayers(nodePoints: any, edgeLines: EdgeLine[]): void {
    this.cleanSourceAndLayers();
    this.map.addSource('excellent-edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines.filter(({ properties }) => properties.coverageStatus === CoverageLevel.EXCELLENT) as any
      }
    });
    this.map.addSource('good-edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines.filter(({ properties }) => properties.coverageStatus === CoverageLevel.GOOD) as any
      }
    });
    this.map.addSource('usual-edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines.filter(({ properties }) => properties.coverageStatus === CoverageLevel.USUAL) as any
      }
    });
    this.map.addSource('bad-edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines.filter(({ properties }) => properties.coverageStatus === CoverageLevel.BAD) as any
      }
    });
    this.map.addSource('unknown-edges', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: edgeLines.filter(({ properties }) => properties.coverageStatus === CoverageLevel.UNKNOWN) as any
      }
    });
    this.map.addSource('nodes', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: nodePoints
      }
    });
    this.map.addLayer({
      id: 'excellent-edges',
      type: 'line',
      source: 'excellent-edges',
      paint: {
        'line-width': 3,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'good-edges',
      type: 'line',
      source: 'good-edges',
      paint: {
        'line-width': 3,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'usual-edges',
      type: 'line',
      source: 'usual-edges',
      paint: {
        'line-width': 3,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'bad-edges',
      type: 'line',
      source: 'bad-edges',
      paint: {
        'line-width': 3,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'unknown-edges',
      type: 'line',
      source: 'unknown-edges',
      paint: {
        'line-width': 3,
        'line-color': ['get', 'color'],
        'line-dasharray': ['get', 'dash-line']
      }
    });
    this.map.addLayer({
      id: 'nodes',
      type: 'circle',
      source: 'nodes',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    });
    if( !!this.currentPopup && !!this.selectedIntegrationCode ) {
      this.paintActiveNode(this.selectedIntegrationCode);
    }
  }

  private cleanSourceAndLayers(): void {
    this.coverageLevels.forEach(coverageLevel => {
      if( !!this.map.getLayer(`${ coverageLevel.toLowerCase() }-edges`) ) {
        this.map.removeLayer(`${ coverageLevel.toLowerCase() }-edges`);
      }
      if( !!this.map.getSource(`${ coverageLevel.toLowerCase() }-edges`) ) {
        this.map.removeSource(`${ coverageLevel.toLowerCase() }-edges`);
      }
    });
    if( !!this.map.getLayer('nodes') ) {
      this.map.removeLayer('nodes');
    }
    if( !!this.map.getSource('nodes') ) {
      this.map.removeSource('nodes');
    }
  }

  private createListenersAndPopups(): void {
    this.map.on('mouseenter', 'nodes', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'nodes', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.addControl(new MapLayerControl(document.getElementById('map-legend')), 'bottom-right');
    this.map.addControl(new MapLayerControl(document.getElementById('map-layer-control-filters')));

    this.map.off('click', 'nodes', this.nodesListener);

    this.map.on('click', 'nodes', this.nodesListener);

  }

  private showPopup(coordinates: [number, number], context): void {
    const popupContent = `
      <div>
        <p class="font-semibold text-lg text-black">${ context.identifier }</p>
        <div class="flex flex-column align-items-start">
          <span>${ this.tran.translate('nettopo.totalCommunications') }: ${ context.totalCommunications }</span>
          ${ context.coverage !== 'unknown' ? `<span>${ this.tran.translate('nettopo.coverage') }: ${ context.coverage }</span>` : '' }
          ${ context.coverage !== 'unknown' ? `<span>${ this.tran.translate('nettopo.distance') }: ${ context.distance }</span>` : '' }
        </div>
        <div class="flex align-items-center justify-content-between mt-4 gap-4">
          <button id="details-button" class="custom-btn">${ this.tran.translate('misc.viewDetails') }<i class="pi pi-info-circle ml-2"></i></button>
          <button id="load-button" class="custom-btn">${ this.tran.translate('nettopo.loadSources') }<i class="pi pi-share-alt ml-2"></i></button>
        </div>
      </div>
    `;

    this.currentPopup = new mapboxgl.Popup({ offset: 25, className: 'node-popup', maxWidth: '300px' })
      .setLngLat(coordinates)
      .setHTML(popupContent)
      .addTo(this.map);

    const detailsButton: any = document.getElementById('details-button');
    detailsButton.disabled = !this.selectedDeviceUuid;
    detailsButton.addEventListener('click', () => {
      this.showNodeDetails();
    });

    const loadButton: any = document.getElementById('load-button');
    loadButton.disabled = this.alreadyLoadedNodes.find(node => node === this.selectedIntegrationCode);
    loadButton.addEventListener('click', () => {
      this.retrieveChildNodes();
    });

    this.currentPopup.on('close', () => {
      this.showNodeDialog = false;
      this.restartActiveNodes();
    });
  }

  public closePopup(): void {
    if( !!this.currentPopup ) {
      this.currentPopup.remove();
      this.currentPopup = undefined;
    }
  }

  public showNodeDetails(): void {
    this.retrieveChildNodes(false, true);
  }

  public retrieveChildNodes(closePopup: boolean = true, openDetails: boolean = false): void {
    if( closePopup ) { this.closePopup(); }
    if( !this.alreadyLoadedNodes.find(node => node === this.selectedIntegrationCode) ) {
      this.mapIsLoading = true;
      this.http.post<any>('/catalog/reports/nettopo', this.buildQuery(this.selectedIntegrationCode)).subscribe(response => {
        this.alreadyLoadedNodes.push(this.selectedIntegrationCode);
        this.drawData(response);
        this.applyLayerFilter(this.layerForm.get('visibleLayer').value);
        if( openDetails ) { this.showNodeDialog = true; }
      });
    } else {
      if( openDetails ) { this.showNodeDialog = true; }
    }
  }

  private buildQuery(integrationIdentifier?: string): any {
    const baseQuery = {
      aggs: {
        edges: {
          terms: { field: this.isFirstLoad ? 'to.keyword' : '@edge.keyword', size: 100000000 },
          aggs: { max_doc: { top_hits: { size: 1, sort: [{ '@timestamp': { order: 'desc' } }] } } }
        }
      },
      size: 0,
      _source: { excludes: [] },
      stored_fields: ['*'],
      script_fields: {},
      docvalue_fields: [{ field: '@timestamp', format: 'date_time' }],
      query: { bool: { must: [], filter: [], should: [], must_not: [] } }
    };

    const rangeDates = this.currentFilters.rangeDates;
    if( rangeDates && rangeDates.length > 1 ) {
      if( !rangeDates[1] ) {
        rangeDates[1] = moment.utc().toDate();
      }
      baseQuery.query.bool.must.push({
        range: {
          '@timestamp': {
            format: 'strict_date_optional_time',
            gte: rangeDates[0],
            lte: rangeDates[1]
          }
        }
      });
    }

    if( this.isFirstLoad ) {
      baseQuery.query.bool.filter.push({
        bool: {
          should: [
            {
              match: {
                throughGw: true
              }
            }
          ],
          minimum_should_match: 1
        }
      });
    }

    if( this.currentFilters.tenantUuids ) {
      const baseTenantFilter = { bool: { minimum_should_match: 1, should: [] } };
      this.currentFilters.tenantUuids.forEach(tenant => {
        baseTenantFilter.bool.should.push({ match_phrase: { 'tenant.keyword': tenant } });
      });
      baseQuery.query.bool.must.push(baseTenantFilter);
    }

    if( this.currentFilters.codes ) {
      const baseCodesFilter = { bool: { should: [], minimum_should_match: 1 } };
      this.currentFilters.codes.forEach(code => {
        baseCodesFilter.bool.should.push({
          bool: {
            should: [
              {
                match_phrase: {
                  'fromIntegrationCode.keyword': code
                }
              }
            ],
            minimum_should_match: 1
          }
        });
      });
      baseQuery.query.bool.filter.push(baseCodesFilter);
    }

    if( !this.isFirstLoad && !!integrationIdentifier ) {
      baseQuery.query.bool.filter.push({
        bool: {
          should: [
            {
              match_phrase: {
                'to.keyword': integrationIdentifier
              }
            }
          ],
          minimum_should_match: 1
        }
      });
    }

    return baseQuery;

  }

  resetActiveLayer(): void {
    this.layerForm.get('visibleLayer').setValue(CoverageLevel.ANY);
  }

  canDeactivate(): Promise<boolean> {
    this.displayDetails = false;
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 0);
    });
  }

  ngOnDestroy(): void {
    this.activeLayerSubscription.unsubscribe();
    // this.map?.remove(); //! Condición de carrera
  }

  public readonly CoverageLevel = CoverageLevel;

}
